import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import api from '@/lib/api';
import { BASE, Box, Pager, inp } from './ui';

const Cell = ({ v }) => {
  if (v && typeof v === 'object') return <span className="rounded bg-slate-200 px-1 text-[10px]">{v._masked ? 'mascarado' : 'cifrado'}</span>;
  if (v === '' || v === null || v === undefined) return <span className="text-slate-300">·</span>;
  return <span>{String(v).slice(0, 80)}</span>;
};

function TabelaView({ t }) {
  const [pagina, setPagina] = useState(1);
  const [aluno, setAluno] = useState('');
  const [ano, setAno] = useState('');
  const [filtro, setFiltro] = useState({});
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`${BASE}/modulos/tabela/${encodeURIComponent(t.tabela)}`, { params: { pagina, limite: 50, ano: filtro.ano || undefined },
      headers: filtro.aluno ? { 'X-Acervo-Aluno': encodeURIComponent(filtro.aluno) } : {} })
      .then(({ data }) => setD(data)).catch((e) => setD({ items: [], colunas: [], erro: e?.response?.data?.detail || 'Erro na consulta.' }));
  }, [t, pagina, filtro]);
  const frio = t.tier === 'ARQUIVO_FRIO';
  return (
    <Box testid="acervo-modulo-tabela">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <b className="text-sm" data-testid="acervo-modulo-tabela-nome">{t.tabela}</b>
        <span className="text-xs text-slate-500">{frio ? 'arquivo frio (leitura sequencial)' : 'linha a linha'} · {t.carregadas.toLocaleString('pt-BR')} linhas · {t.bloqueadas_2026} fora do corte</span>
        {!frio && (
          <form className="ml-auto flex gap-2" onSubmit={(e) => { e.preventDefault(); setPagina(1); setFiltro({ aluno: aluno.trim(), ano: ano.trim() }); }}>
            <input className={`${inp} w-32`} value={aluno} onChange={(e) => setAluno(e.target.value)} placeholder="AlunoID" data-testid="acervo-modulo-filtro-aluno" />
            <input className={`${inp} w-20`} value={ano} onChange={(e) => setAno(e.target.value)} placeholder="Ano" data-testid="acervo-modulo-filtro-ano" />
            <button type="submit" className="rounded-lg bg-slate-900 px-3 py-1 text-xs text-white" data-testid="acervo-modulo-filtrar">Filtrar</button>
          </form>)}
      </div>
      {d?.erro && <p className="text-xs text-rose-700" data-testid="acervo-modulo-erro">{d.erro}</p>}
      {!d ? <p className="text-xs">Carregando…</p> : (
        <div className="max-h-[460px] overflow-auto">
          <table className="w-full whitespace-nowrap text-[11px]"><thead className="sticky top-0 bg-white"><tr className="text-left text-slate-500"><th className="pr-2">Linha</th>{!frio && <th className="pr-2">Ano ref.</th>}{d.colunas.map((c) => <th key={c} className="pr-2">{c}</th>)}</tr></thead>
            <tbody>{d.items.map((r) => <tr key={r.linha} className="border-t" data-testid="acervo-modulo-linha"><td className="pr-2 font-mono">{r.linha}</td>{!frio && <td className="pr-2">{r.ano_referencia || '—'}</td>}
              {d.colunas.map((c) => <td key={c} className="pr-2"><Cell v={r.campos[c]} /></td>)}</tr>)}</tbody></table>
        </div>)}
      <Pager d={d} pagina={pagina} setPagina={setPagina} testid="acervo-modulo-pager" />
      {d?.origem && <p className="mt-1 break-all text-[11px] text-slate-500">Fonte: {d.origem.zip} › {d.origem.arquivo} · SHA-256 {d.origem.sha256_xlsx?.slice(0, 12)}… · {d.obs}</p>}
    </Box>
  );
}

export function ModulosTab() {
  const [d, setD] = useState(null);
  const [mod, setMod] = useState('');
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get(`${BASE}/modulos`).then(({ data }) => { setD(data); setMod(data.items[0]?.modulo || ''); }).catch(() => setD({ items: [] })); }, []);
  if (!d) return <Box>Carregando…</Box>;
  const atual = d.items.find((m) => m.modulo === mod);
  return (
    <div className="space-y-3">
      <Box testid="acervo-modulos">
        <p className="mb-2 text-xs text-slate-500">{d.corte}{d.ocultos_por_perfil?.length ? ` Módulos restritos à Direção ocultos para o seu perfil: ${d.ocultos_por_perfil.join(', ')}.` : ''}</p>
        <div className="flex flex-wrap gap-2">
          {d.items.map((m) => (
            <button key={m.modulo} type="button" onClick={() => { setMod(m.modulo); setSel(null); }} data-testid={`acervo-modulo-${m.modulo.normalize('NFD').replace(/[^a-z]/gi, '')}`}
              className={`rounded-xl border px-3 py-2 text-left text-xs transition-colors ${mod === m.modulo ? 'border-slate-900 bg-slate-900 text-white' : 'bg-white hover:bg-amber-50'}`}>
              <span className="flex items-center gap-1 font-semibold capitalize">{m.restrito_direcao && <Lock className="h-3 w-3" />}{m.modulo.replace('_', ' de ')}</span>
              <span>{m.tabelas.length} tabelas · {m.carregadas.toLocaleString('pt-BR')} linhas</span>
            </button>))}
        </div>
        {atual && (
          <table className="mt-3 w-full text-xs" data-testid="acervo-modulo-tabelas"><thead><tr className="text-left text-slate-500"><th>Tabela</th><th>Armazenamento</th><th>Linhas origem</th><th>Carregadas</th><th>Fora do corte (2026+ / fora do acervo)</th></tr></thead>
            <tbody>{atual.tabelas.map((t) => (
              <tr key={t.tabela} onClick={() => setSel(t)} className={`cursor-pointer border-t hover:bg-amber-50 ${sel?.tabela === t.tabela ? 'bg-amber-50' : ''}`} data-testid={`acervo-tabela-${t.tabela}`}>
                <td className="py-1 font-medium">{t.tabela}</td><td>{t.tier === 'ARQUIVO_FRIO' ? 'frio' : 'linha a linha'}</td><td>{t.linhas_origem?.toLocaleString('pt-BR')}</td><td>{t.carregadas.toLocaleString('pt-BR')}</td><td>{t.bloqueadas_2026}</td></tr>))}</tbody></table>)}
      </Box>
      {sel && <TabelaView t={sel} />}
    </div>
  );
}
